import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  getInrichtingDetail, getGebieden, getGeplandeMelding,
  annuleerGeplandeWijziging, trekGeplandeMeldingIn,
} from '../api/api';
import { fmtId, fmtDatumNL, kenmerkTypeLabel } from '../util/format';
import { labelVoorDoel } from '../util/doelen';
import Modal from '../components/Modal';
import KaartMetLegenda from '../components/KaartMetLegenda';
import { waardenUitPayload, puttenUitPayload } from '../components/meldingModel';

/* Wat er klaarstaat, in een venster.

   Elke regel in de tijdlijn opent hetzelfde soort venster: een afgesloten
   recht, het lopende recht, en — hier — een klaarstaande melding of een
   klaarstaand wijzigingsbesluit. Je klikt om te zien wat er aankomt, niet om
   het meteen te wijzigen. Bewerken is een knop in dit venster en brengt je naar
   de bewerkpagina; dat is een andere handeling, met een andere plek.

   Wat er staat is de situatie zoals die onder dát recht geldt: de putten waar
   in die periode uit onttrokken mag worden, en de voorwaarden die er dan bij
   horen. Dus geen verschil met vandaag, geen "blijft" of "komt erbij" — dat
   gaat over de weg ernaartoe en niet over het recht. Een wijzigingsbesluit
   beschrijft in de wachtkamer wel een verschil (deze putten erbij, die eruit),
   maar dat is de vorm waarin het is opgeslagen; wat het bétekent is de
   uitkomst, en dat is wat je hier ziet. Bij een besluit verderop in de rij
   wordt dat uitgerekend vanaf de eerdere besluiten, want die gaan eerder in. */

function Veld({ label, children }) {
  return (
    <div className="field">
      <span className="field-label">{label}</span>
      <span className="field-value">{children === null || children === undefined || children === '' ? '—' : children}</span>
    </div>
  );
}

export default function KlaarstaandModal({ soort = 'besluit' }) {
  const { id, geplandId } = useParams();
  const navigate = useNavigate();
  const context = useOutletContext() || {};

  const [detail, setDetail] = useState(null);
  const [melding, setMelding] = useState(null);
  const [gebieden, setGebieden] = useState([]);
  const [laden, setLaden] = useState(true);
  const [fout, setFout] = useState(null);
  const [bezig, setBezig] = useState(false);

  const sluit = () => navigate(`/beregening/inrichtingen/${id}`);

  useEffect(() => {
    let actief = true;
    (async () => {
      setLaden(true);
      try {
        const [d, geb, m] = await Promise.all([
          getInrichtingDetail(id),
          getGebieden(undefined, true),
          soort === 'melding' ? getGeplandeMelding(geplandId) : Promise.resolve(null),
        ]);
        if (!actief) return;
        setDetail(d.data);
        setGebieden(geb.data || []);
        setMelding(m ? m.data : null);
        setFout(null);
      } catch {
        if (actief) setFout('Kon dit niet laden.');
      } finally {
        if (actief) setLaden(false);
      }
    })();
    return () => { actief = false; };
  }, [id, geplandId, soort]);

  const sluitEnHerlaad = async () => {
    if (context.herlaad) await context.herlaad();
    sluit();
  };

  if (laden) {
    return (
      <Modal isOpen onClose={sluit} maxWidth={900}>
        <div style={{ padding: 40, textAlign: 'center' }}><span className="spinner" /></div>
      </Modal>
    );
  }
  if (fout || !detail) {
    return (
      <Modal isOpen onClose={sluit} maxWidth={900}>
        <div className="alert alert-error">{fout || 'Niet gevonden.'}</div>
      </Modal>
    );
  }

  const gedeeld = { id, geplandId, detail, gebieden, bezig, setBezig, setFout, navigate, sluit, sluitEnHerlaad };
  return soort === 'melding'
    ? <MeldingVenster {...gedeeld} melding={melding} />
    : <BesluitVenster {...gedeeld} />;
}

/* ── Een klaarstaand wijzigingsbesluit ──────────────────────────────────── */
function BesluitVenster({ id, geplandId, detail, gebieden, bezig, setBezig, setFout,
  navigate, sluit, sluitEnHerlaad }) {
  const wachtrij = detail.geplande_wijzigingen || [];
  const positie = wachtrij.findIndex((x) => x.gepland_id === geplandId);
  const g = positie >= 0 ? wachtrij[positie] : null;

  if (!g) {
    return (
      <Modal isOpen onClose={sluit} maxWidth={900}>
        <div className="alert alert-error">
          Dit geplande wijzigingsbesluit bestaat niet meer. Mogelijk is het inmiddels
          ingegaan of geannuleerd.
        </div>
      </Modal>
    );
  }

  const later = wachtrij.slice(positie + 1);

  const info = new Map();
  (detail.putten || []).forEach((p) => info.set(p.put_id, p));

  /* De putten waar dit recht over gaat: de putten van nu, met alle besluiten
     tot en met dit besluit erop toegepast. De besluiten vóór dit besluit gaan
     eerder in, dus die tellen mee — anders zou je een put tonen die op de dag
     van dit besluit allang vertrokken is.

     Welke er nog geboord moeten worden houden we apart bij: dat zegt iets over
     die periode zelf, niet over het verschil met vandaag. */
  let onderRecht = (detail.putten || [])
    .filter((p) => p.actief !== false && !p.aankomend_vanaf)
    .map((p) => p.put_id);
  const teBoren = new Set();
  wachtrij.slice(0, positie + 1).forEach((e) => {
    const eruit = new Set(e.put_ids_verwijderen || []);
    onderRecht = onderRecht.filter((pid) => !eruit.has(pid));
    (e.putten_toevoegen || []).forEach((t) => {
      if (!t.put_id) return;
      if (!onderRecht.includes(t.put_id)) onderRecht.push(t.put_id);
      if (t.aangemaakt_voor_besluit) teBoren.add(t.put_id);
    });
  });

  const rijen = onderRecht
    .map((pid) => info.get(pid))
    .filter(Boolean)
    .map((put) => ({ put, nieuw: teBoren.has(put.put_id) }));

  const intrekken = async () => {
    const staart = later.length
      ? `\n\nLet op: er ${later.length === 1 ? 'staat' : 'staan'} hierna nog `
        + `${later.length} besluit${later.length === 1 ? '' : 'en'} klaar `
        + `(${later.map((l) => l.wijzigingsbesluitnummer).join(', ')}). `
        + `${later.length === 1 ? 'Dat blijft' : 'Die blijven'} staan.`
      : '';
    if (!window.confirm(
      `Weet je zeker dat je het klaarstaande wijzigingsbesluit "${g.wijzigingsbesluitnummer}" `
      + `van ${fmtDatumNL(g.ingangsdatum)} wilt intrekken?${staart}`)) return;
    setBezig(true);
    try {
      await annuleerGeplandeWijziging(id, geplandId);
      await sluitEnHerlaad();
    } catch (e) {
      setFout(e.response?.data?.detail || e.message);
      setBezig(false);
    }
  };

  return (
    <Venster
      sluit={sluit}
      kop="Klaarstaand wijzigingsbesluit"
      id={id}
      kenmerkLabel="Wijzigingsbesluit"
      kenmerk={g.wijzigingsbesluitnummer}
      datumregel={`Gaat in op ${fmtDatumNL(g.ingangsdatum)}`}
      rijen={rijen}
      gebieden={gebieden}
      navigate={navigate}
      gegevens={(
        <>
          <Veld label="Doelen">
            {g.doelen && g.doelen.length ? g.doelen.map(labelVoorDoel).join(', ') : null}
          </Veld>
          <Veld label="Ingangsdatum">{fmtDatumNL(g.ingangsdatum)}</Veld>
          <Veld label="Bedrijfsomvang (ha)">{g.bedrijfsomvang_ha}</Veld>
          <Veld label="Vergund uurdebiet (m³)">{g.vergund_debiet_uur}</Veld>
          <Veld label="Vergund maanddebiet (m³)">{g.vergund_debiet_maand}</Veld>
          <Veld label="Vergund kwartaaldebiet (m³)">{g.vergund_debiet_kwartaal}</Veld>
          <Veld label="Vergund jaardebiet (m³)">{g.vergund_debiet_jaar}</Veld>
          <Veld label="Opmerkingen">{g.opmerkingen}</Veld>
          <Veld label="Ingevoerd door">{g.aangemaakt_door}</Veld>
        </>
      )}
      bezig={bezig}
      opIntrekken={intrekken}
      intrekkenLabel="Wijzigingsbesluit intrekken"
      opBewerken={() => navigate(`/beregening/inrichtingen/${id}/wijzigingsbesluit/${geplandId}`)}
    />
  );
}

/* ── Een klaarstaande melding ───────────────────────────────────────────── */
function MeldingVenster({ id, geplandId, melding, gebieden, bezig, setBezig, setFout,
  navigate, sluit, sluitEnHerlaad }) {
  if (!melding) {
    return (
      <Modal isOpen onClose={sluit} maxWidth={900}>
        <div className="alert alert-error">
          Deze klaarstaande melding bestaat niet meer. Mogelijk is hij inmiddels
          ingegaan of ingetrokken.
        </div>
      </Modal>
    );
  }
  /* Een melding beschrijft de hele nieuwe situatie: de putten die erin staan
     zijn precies de putten waar het recht straks over gaat. */
  const waarden = waardenUitPayload(melding.payload);
  const rijen = puttenUitPayload(melding.payload).map((p) => ({
    put: {
      put_id: p.put_id,
      x_rd: p.x_rd,
      y_rd: p.y_rd,
      perceel: p.perceel,
      opgegeven_diepte_m: p.opgegeven_diepte_m || null,
      diepte_bv_filter_m: null,
    },
    nieuw: p.soort === 'nieuw',
  }));

  const intrekken = async () => {
    if (!window.confirm(
      `Weet je zeker dat je de klaarstaande melding "${melding.kenmerk}" `
      + `van ${fmtDatumNL(melding.inwerkingtreding)} wilt intrekken?`)) return;
    setBezig(true);
    try {
      await trekGeplandeMeldingIn(geplandId);
      await sluitEnHerlaad();
    } catch (e) {
      setFout(e.response?.data?.detail || e.message);
      setBezig(false);
    }
  };

  return (
    <Venster
      sluit={sluit}
      kop="Klaarstaande melding"
      id={id}
      kenmerkLabel={kenmerkTypeLabel(melding.kenmerk_type)}
      kenmerk={melding.kenmerk}
      datumregel={`Gaat in op ${fmtDatumNL(melding.inwerkingtreding)}`}
      rijen={rijen}
      gebieden={gebieden}
      navigate={navigate}
      gegevens={(
        <>
          <Veld label="Doel">{waarden.doel ? labelVoorDoel(waarden.doel) : null}</Veld>
          <Veld label="Meldingsdatum">{fmtDatumNL(melding.meldingsdatum)}</Veld>
          <Veld label="Inwerkingtreding">{fmtDatumNL(melding.inwerkingtreding)}</Veld>
          <Veld label="Max pompcapaciteit (m³/u)">{waarden.pompcapaciteit}</Veld>
          <Veld label="Bedrijfsomvang (ha)">{waarden.hectare}</Veld>
          <Veld label="Ingevoerd door">{melding.aangemaakt_door}</Veld>
        </>
      )}
      bezig={bezig}
      opIntrekken={intrekken}
      intrekkenLabel="Melding intrekken"
      opBewerken={() => navigate(`/beregening/inrichtingen/${id}/melding-verwerken?gepland=${geplandId}`)}
    />
  );
}

/* ── De vorm die ze delen ───────────────────────────────────────────────── */
function Venster({ sluit, kop, id, kenmerkLabel, kenmerk, datumregel,
  rijen = [], gebieden, navigate, gegevens, bezig,
  opIntrekken, intrekkenLabel, opBewerken }) {
  const naarPut = (put_id) => (put_id
    ? () => navigate(`/beregening/putten/${put_id}`)
    : undefined);
  return (
    <Modal isOpen onClose={sluit} maxWidth={900}>
      <div>
        <div style={{ marginBottom: 22 }}>
          <div className="historie-eyebrow">{kop}</div>
          <h2 style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 4px' }}>
            {fmtId(id)}
          </h2>
          <div style={{ fontSize: 15, color: 'var(--text)', marginBottom: 2 }}>
            <span style={{ color: 'var(--text-muted)' }}>{kenmerkLabel} </span>
            <span className="mono">{kenmerk}</span>
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-soft)' }}>{datumregel}</div>
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header">
            <h3 className="card-title">
              Putten <span className="chip" style={{ marginLeft: 8 }}>{rijen.length}</span>
            </h3>
          </div>
          {rijen.length > 0 ? (
            <>
              <KaartMetLegenda
                punten={rijen.map((r) => ({
                  put_id: r.put.put_id,
                  x_rd: r.put.x_rd,
                  y_rd: r.put.y_rd,
                  perceel: r.put.perceel,
                  // Een put die nog geboord moet worden bestaat in die periode
                  // wel als recht, maar nog niet in het veld.
                  soort: r.nieuw ? 'nieuw' : 'gekoppeld',
                  onClick: naarPut(r.put.put_id),
                }))}
                overlays={gebieden}
                hoogte={300}
              />
              <div style={{ marginTop: 16 }} />
              <table className="table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Coördinaten (RD)</th>
                    <th>Opgegeven diepte</th>
                    <th>Filterdiepte (bovenkant)</th>
                    <th>Perceel</th>
                  </tr>
                </thead>
                <tbody>
                  {rijen.map((r, i) => (
                    <tr
                      key={r.put.put_id || `r${i}`}
                      className={r.put.put_id ? 'clickable' : undefined}
                      onClick={naarPut(r.put.put_id)}
                    >
                      <td><span className="mono">{r.put.put_id ? fmtId(r.put.put_id) : '—'}</span></td>
                      <td>{Math.round(r.put.x_rd)}, {Math.round(r.put.y_rd)}</td>
                      <td>{r.put.opgegeven_diepte_m ?? '—'}</td>
                      <td>{r.put.diepte_bv_filter_m ?? '—'}</td>
                      <td>{r.put.perceel || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              Onder dit recht hoort geen enkele put.
            </p>
          )}
        </div>

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><h3 className="card-title">Gegevens</h3></div>
          <div className="field-grid cols-3" style={{ gap: 12 }}>{gegevens}</div>
        </div>

        <div className="mf-voet">
          <button className="btn btn-danger" onClick={opIntrekken} disabled={bezig}>
            {intrekkenLabel}
          </button>
          <button className="btn btn-ghost" onClick={sluit} disabled={bezig}>Sluiten</button>
          <button className="btn btn-primary" onClick={opBewerken} disabled={bezig}>Bewerken</button>
        </div>
      </div>
    </Modal>
  );
}
